import { useEffect, useState, useRef, useMemo } from 'react';
import { tokens } from "../../theme";
import {
    useTheme, Box, Grid
} from '@mui/material';

import { createChart, PriceScaleMode } from 'lightweight-charts';

import { __fetching_data__ } from '../../components/api';

// ─── Skeleton Components ──────────────────────────────────────────────────────
function WaveSkeleton({ delay = 0 }) {
  return (
    <div className="w-full h-full bg-[#0f172a] rounded-lg overflow-hidden relative">
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      <div className="absolute inset-0 flex flex-col justify-between p-3">
        <div className="flex gap-2">
          <div className="h-2 rounded-full bg-slate-800 w-1/3" />
          <div className="h-2 rounded-full bg-slate-800 w-1/5" />
        </div>
        <div className="flex-1 my-3 rounded bg-slate-800/60" />
        <div className="flex gap-3 justify-between">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-2 rounded-full bg-slate-800 flex-1" />
          ))}
        </div>
      </div>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.07) 40%, rgba(125,211,252,0.15) 50%, rgba(56,189,248,0.07) 60%, transparent 100%)",
          animation: "shimmer 1.8s ease-in-out infinite",
          animationDelay: `${delay}s`,
        }}
      />
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Align a dataset to a master timeline (derived from CLOSE).
 * Dates that exist in masterTimes but not in data will be filled with value 0.
 */
const alignToMasterTimeline = (data, masterTimes) => {
    const dataMap = data.reduce((acc, item) => {
        acc[item.time] = item.value;
        return acc;
    }, {});

    let lastKnownValue = null;
    return masterTimes.map(time => {
        if (dataMap[time] !== undefined) {
            lastKnownValue = dataMap[time];
        }
        return {
            time,
            value: lastKnownValue !== null ? lastKnownValue : 0,
        };
    });
};

function createSeries(
    arr,
    selectedYear,
    chartInstanceRefs,
    seriesSetting,
    color,
    serieInstanceRefs,
    key,
    seriesType,
    seriesParam,
    idx,
    masterTimes   // ← master timeline from CLOSE series
) {
    const raw  = prepareData(arr, selectedYear, seriesType);
    // If masterTimes provided, align & fill missing dates with 0
    const data = masterTimes && masterTimes.length > 0
        ? alignToMasterTimeline(raw, masterTimes)
        : raw;

    const series = chartInstanceRefs.current[idx].addLineSeries(seriesSetting);
    series.setData(data);
    series.applyOptions({ color, lastValueVisible: false, visible: false });
    serieInstanceRefs.current[seriesParam][key] = series;
}

const compareDatetime = (a, b, field = 'DateTime') => {
    const datetimeA = new Date(a[field]);
    const datetimeB = new Date(b[field]);
    return datetimeA - datetimeB;
};

const filterByRange = (data, range) => {
    if (range === 'ALL') return data;

    const currentDate = new Date();
    let fromDate;

    switch (range) {
        case '3M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 3);
            break;
        case '6M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 6);
            break;
        case '1Y':
            fromDate = new Date(currentDate);
            fromDate.setFullYear(currentDate.getFullYear() - 1);
            break;
        case 'YTD':
            fromDate = new Date(currentDate.getFullYear(), 0, 1);
            break;
        default:
            console.warn(`Unsupported range: ${range}`);
            return data;
    }

    return data.filter(item => {
        const rawDate = item.Date ?? item.date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate >= fromDate && itemDate <= currentDate;
    });
};

const generateData = (data, date_column = 'DateTime', field) => {
    const cdata = data
        .filter(d => d[date_column] != null && d[field] != null && !isNaN(parseFloat(d[field])))
        .map(d => {
            const dd = new Date(d[date_column]);
            const year  = dd.getFullYear();
            const month = String(dd.getMonth() + 1).padStart(2, '0');
            const day   = String(dd.getDate()).padStart(2, '0');
            return { time: `${year}-${month}-${day}`, value: parseFloat(d[field]) };
        });
    return cdata;
};

const generateUniqueRandomColors = (count) => {
    const colors = [
        "#FFC300", "#8BC34A", "#A8E6CF", "#C9CBCF", "#4BC0C0", "#009688",
        "#FF6384", "#36A2EB", "#FFCE56", "#9966FF", "#FF9F40", "#FFD3B6",
        "#FFAAA5", "#D1C4E9", "#B3E5FC", "#DCEDC8", "#FFF9C4", "#FFECB3"
    ];
    function shuffleArray(array) {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }
    return shuffleArray(colors);
};

const prepareData = (arr, year, valueKey) => {
    const values = filterByRange(arr, year).sort((a, b) => compareDatetime(a, b, 'date'));
    const gen    = generateData(values, 'date', valueKey);
    return Object.values(gen.reduce((acc, item) => {
        acc[item.time] = item;
        return acc;
    }, {}));
};

// ─── Component ────────────────────────────────────────────────────────────────
const PetroleumComponent = () => {
    const theme  = useTheme();
    const colors = tokens(theme.palette.mode);

    const isEmpty = (obj) => Object.keys(obj).length === 0;

    // ── UI state ──
    const [symbolQuery, setSymbolQuery]             = useState("");
    const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);

    // ── Loading state ──
    const [isLoading, setIsLoading] = useState(false);

    // ── Oil type multi-select state ──
    const [showOilTypeDropdown, setShowOilTypeDropdown] = useState(false);
    const oilTypeDropdownRef = useRef(null);

    // ── Chart setup ──
    const chart_names = ['CLOSE', 'EX-REFIN', 'Marketing Margin', 'Oil Fund'];

    const baseChartOptions = {
        width: 500,
        height: 300,
        crosshair: { mode: 1 },
        timeScale: { visible: true, borderColor: theme.palette.divider },
        localization: { locale: "en-US" },
        layout: {
            attributionLogo: false,
            background: { type: 'solid', color: "transparent" },
            textColor: theme.palette.text.primary,
        },
        grid: {
            vertLines: { color: theme.palette.divider },
            horzLines: { color: theme.palette.divider },
        },
        lastValueVisible: false
    };

    const charts_options = [
        { ...baseChartOptions, rightPriceScale: { scaleMargins: { top: 0.3, bottom: 0.2 }, borderVisible: true,  drawTicks: false } },
        { ...baseChartOptions, rightPriceScale: { scaleMargins: { top: 0.3, bottom: 0.2 }, borderVisible: false, drawTicks: true  } },
        { ...baseChartOptions, rightPriceScale: { scaleMargins: { top: 0.3, bottom: 0.2 }, borderVisible: false, drawTicks: true  } },
        { ...baseChartOptions, rightPriceScale: { scaleMargins: { top: 0.3, bottom: 0.2 }, borderVisible: false, drawTicks: true  } },
    ];

    const serieInstanceRefs = useRef({ close: {}, ex_refin: {}, mkt_margin: {}, oil_fund: {} });
    const [seriesSetting]   = useState({
        priceSeries:  { color: "#ff9966", lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
        mainSeries2:  { color: "#33ffcc", lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
        mainSeries3:  { color: "#ff9966", lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
        mainSeries4:  { color: "#66ffcc", lineWidth: 3, priceLineVisible: false, lastValueVisible: false },
    });

    function getCrosshairDataPoint(series, param) {
        if (!param.time) return null;
        return param.seriesData.get(series) || null;
    }

    function syncCrosshair(chart, series, dataPoint) {
        if (!series) { chart.clearCrosshairPosition(); return; }
        if (dataPoint && dataPoint.value != null && dataPoint.time != null) {
            const ts = typeof dataPoint.time === "number"
                ? dataPoint.time
                : Math.floor(new Date(dataPoint.time).getTime() / 1000);
            try { chart.setCrosshairPosition(dataPoint.value, ts, series); }
            catch (err) { chart.clearCrosshairPosition(); }
        } else { chart.clearCrosshairPosition(); }
    }

    // ── Chart container ──
    const resChartRef          = useRef(null);
    const [chartsReady, setChartsReady]             = useState(false);
    const [chartContainers, setChartContainers]     = useState([]);
    const chartInstanceRefs    = useRef([]);
    const observersRef         = useRef([]);
    const isDisposedRef        = useRef(false);
    const memoizedChartContainers = useMemo(() => chartContainers, [chartContainers]);
    const uniqueRandomColors   = useRef([]);

    useEffect(() => {
        if (!resChartRef.current) return;
        resChartRef.current.style.height = `calc(100vh - 120px)`;
    }, []);

    const handleAddChartContainer = (element) => {
        if (!chartContainers.includes(element) && element) {
            setChartContainers(prev => [...prev, element]);
        }
    };

    useEffect(() => {
        if (chartContainers.length !== 4) return;

        isDisposedRef.current = false;
        observersRef.current.forEach(ro => ro.disconnect());
        observersRef.current = [];

        chartContainers.forEach((container, index) => {
            const chart = createChart(container, charts_options[index] || charts_options[0]);
            chart.applyOptions({
                watermark: {
                    visible: true, fontSize: 30,
                    horzAlign: 'center', vertAlign: 'center',
                    color: theme.palette.chart?.watermark,
                    text: chart_names[index],
                },
                rightPriceScale: { visible: true },
                leftPriceScale: {
                    visible: true, mode: PriceScaleMode.Normal,
                    autoScale: false, invertScale: false,
                    alignLabels: false, minValue: -1, maxValue: 1,
                },
            });
            chartInstanceRefs.current[index] = chart;

            const ro = new ResizeObserver((entries) => {
                if (isDisposedRef.current) return;
                if (entries.length === 0 || entries[0].target !== container) return;
                const c = chartInstanceRefs.current[index];
                if (!c) return;
                try {
                    const newRect = entries[0].contentRect;
                    c.resize(newRect.width, newRect.height);
                } catch (e) {
                    ro.disconnect();
                }
            });
            ro.observe(container);
            observersRef.current.push(ro);
        });

        setChartsReady(true);

        return () => {
            isDisposedRef.current = true;
            observersRef.current.forEach(ro => ro.disconnect());
            observersRef.current = [];
            chartInstanceRefs.current.forEach((chart, idx) => {
                if (chart) {
                    try { chart.remove(); } catch (e) { }
                    chartInstanceRefs.current[idx] = null;
                }
            });
            setChartsReady(false);
        };
    }, [memoizedChartContainers]);

    // ── Params & data ──
    const [paramsQuery, setParamsQuery]                       = useState({ symbol: "", queryType: 3 });
    const [optionsOilType, setOpeionsOilType]                 = useState([]);
    const [selectedOptionsOilType, setSelectedOptionsOilType] = useState([]);
    const [groupByPetroleumName, setGroupByPetroleumName]     = useState({});
    const [symbolPrice, setSymbolPrice]                       = useState({});
    const [isSearch, setIsSearch]                             = useState(false);
    const [symbols, setSymbols]                               = useState([]);
    const [selectedYear, setSelectedYear]                     = useState('3M');

    const filteredSymbols = symbols.filter(s =>
        s.toLowerCase().includes(symbolQuery.toLowerCase())
    );

    // ── Close oil type dropdown on outside click ──
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (oilTypeDropdownRef.current && !oilTypeDropdownRef.current.contains(e.target)) {
                setShowOilTypeDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Toggle oil type selection ──
    const handleToggleOilType  = (opt) => {
        setSelectedOptionsOilType(prev =>
            prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]
        );
    };

    const handleClearOilType = () => setSelectedOptionsOilType([]);

    // ── Fetch symbol list ──
    useEffect(() => {
        const fetchSymbols = async () => {
            try {
                setIsLoading(true);
                const response = await __fetching_data__({
                    url: `${window.config.base_api_url}/petroleum_symbol_list`,
                    method: "GET"
                });
                if (response.status) {
                    response.data.forEach(val => {
                        setSymbols(prev => [...prev, val.SymbolName]);
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSymbols();
    }, []);

    useEffect(() => { setVisibleSeries(); }, [selectedOptionsOilType]);

    useEffect(() => {
        if (isSearch) { clearAllSeries(); get_data(); }
    }, [isSearch]);

    useEffect(() => {
        if (chartsReady) { clearAllSeries(); get_data(); }
    }, [selectedYear, chartsReady]);

    useEffect(() => {
        if (chartsReady) { setData2Charts(); }
    }, [groupByPetroleumName, symbolPrice, chartsReady]);

    const get_data = async () => {
        try {
            setIsLoading(true);
            const response = await __fetching_data__({
                url: `${window.config.base_api_url}/petroleum_report?${new URLSearchParams(paramsQuery)}`,
                method: "GET"
            });
            if (!response.status) return;

            let groupedDataOilType = groupByPetroleumName;
            let sortedDataPrice    = symbolPrice;

            if (isEmpty(groupedDataOilType) && response.data2) {
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 2);

                groupedDataOilType = response.data2.reduce((group, product) => {
                    const name = product.name.trim();
                    group[name] = group[name] || [];
                    group[name].push(product);
                    return group;
                }, {});

                groupedDataOilType = Object.fromEntries(
                    Object.entries(groupedDataOilType).filter(([_, arr]) =>
                        arr.some(product => new Date(product.date) >= oneMonthAgo)
                    )
                );
                setGroupByPetroleumName(groupedDataOilType);
                uniqueRandomColors.current = generateUniqueRandomColors(Object.keys(groupedDataOilType).length);
            }

            if (response.data) {
                sortedDataPrice = [...response.data].sort((a, b) => compareDatetime(a, b, 'Date'));
                setSymbolPrice(sortedDataPrice);
            }
        } catch (error) {
            console.error("Error fetching petroleum report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const setData2Charts = () => {
        try {
            if (!chartInstanceRefs.current[0]) return;

            const hasPriceData = Array.isArray(symbolPrice) && symbolPrice.length > 0;
            const hasOilData   = !isEmpty(groupByPetroleumName);
            if (!hasPriceData && !hasOilData) return;

            const optionOilKeys = Object.keys(groupByPetroleumName);
            setOpeionsOilType(optionOilKeys);

            // ── BUILD MASTER TIMELINE from CLOSE ─────────────────────────────
            // All other series will align to these dates; missing dates → 0
            let masterTimes         = [];
            let deduplicatedPriceData = [];

            if (hasPriceData) {
                const priceData       = filterByRange(symbolPrice, selectedYear);
                const priceSeriesData = generateData(priceData, 'Date', 'price');

                deduplicatedPriceData = Object.values(
                    priceSeriesData.reduce((acc, item) => {
                        acc[item.time] = item;
                        return acc;
                    }, {})
                );

                // Sorted unique dates — this is the master timeline
                masterTimes = deduplicatedPriceData
                    .map(d => d.time)
                    .sort();

                const priceSeries = chartInstanceRefs.current[0].addLineSeries(seriesSetting.priceSeries);
                priceSeries.setData(deduplicatedPriceData);
                serieInstanceRefs.current.close['close'] = priceSeries;
            }

            // ── ADD OIL-TYPE SERIES (aligned to masterTimes) ─────────────────
            if (hasOilData) {
                optionOilKeys.forEach((key, idx) => {
                    const color = uniqueRandomColors.current[idx];
                    const arr   = groupByPetroleumName[key];

                    createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries2, color, serieInstanceRefs, key, 'ex_refin',  'ex_refin',   1, masterTimes);
                    createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries3, color, serieInstanceRefs, key, 'marketing', 'mkt_margin', 2, masterTimes);
                    createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries4, color, serieInstanceRefs, key, 'oil_fund',  'oil_fund',   3, masterTimes);
                });
            }

            // ── DUMMY SERIES (keeps chart-0 time scale consistent) ───────────
            if (hasOilData && masterTimes.length > 0) {
                const dummySeries = chartInstanceRefs.current[0].addLineSeries({
                    color: 'transparent', lineWidth: 0,
                    priceLineVisible: false, lastValueVisible: false,
                    crosshairMarkerVisible: false,
                });
                // Use masterTimes so dummy is perfectly aligned with close
                const dummyData = masterTimes.map(time => ({ time, value: 0 }));
                dummySeries.setData(dummyData);
                dummySeries.applyOptions({ visible: false });
                serieInstanceRefs.current.close['__dummy__'] = dummySeries;
            }

            setTimeout(() => {
                chartInstanceRefs.current.forEach(chart => {
                    if (chart) chart.timeScale().fitContent();
                });

                let masterRange = null;
                let maxWidth    = -Infinity;
                chartInstanceRefs.current.forEach(chart => {
                    if (!chart) return;
                    const range = chart.timeScale().getVisibleLogicalRange();
                    if (!range) return;
                    const width = range.to - range.from;
                    if (width > maxWidth) {
                        maxWidth    = width;
                        masterRange = range;
                    }
                });

                if (masterRange) {
                    chartInstanceRefs.current.forEach(chart => {
                        if (chart) chart.timeScale().setVisibleLogicalRange(masterRange);
                    });
                }

                synchronizeCharts();
                setVisibleSeries();
            }, 0);

            setIsSearch(false);
        } catch (error) {
            console.error("Error setData2Charts:", error);
        }
    };

    const setVisibleSeries = () => {
        try {
            optionsOilType.forEach(key => {
                serieInstanceRefs.current.ex_refin[key]?.applyOptions({ visible: false });
                serieInstanceRefs.current.mkt_margin[key]?.applyOptions({ visible: false });
                serieInstanceRefs.current.oil_fund[key]?.applyOptions({ visible: false });
            });
            selectedOptionsOilType.forEach(key => {
                serieInstanceRefs.current.ex_refin[key]?.applyOptions({ visible: true });
                serieInstanceRefs.current.mkt_margin[key]?.applyOptions({ visible: true });
                serieInstanceRefs.current.oil_fund[key]?.applyOptions({ visible: true });
            });
        } catch (error) { }
    };

    const synchronizeCharts = () => {
        try {
            const keys_series   = Object.keys(serieInstanceRefs.current);
            const filteredCharts = chartInstanceRefs.current.filter(Boolean);

            filteredCharts.forEach(chart => {
                try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(); } catch (e) { }
            });

            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                    if (!timeRange) return;
                    filteredCharts.forEach((otherChart, otherIdx) => {
                        if (currentIdx !== otherIdx) {
                            try { otherChart.timeScale().setVisibleLogicalRange(timeRange); }
                            catch (e) { }
                        }
                    });
                });
            });

            filteredCharts.forEach((currentChart, currentIdx) => {
                currentChart.subscribeCrosshairMove(param => {
                    const seriesGroup = serieInstanceRefs.current[keys_series[currentIdx]];
                    if (!seriesGroup) return;
                    const firstKey = Object.keys(seriesGroup)[0];
                    if (!firstKey) return;
                    const dataPoint = getCrosshairDataPoint(seriesGroup[firstKey], param);

                    filteredCharts.forEach((otherChart, otherIdx) => {
                        if (currentIdx !== otherIdx) {
                            const otherSeriesGroup = serieInstanceRefs.current[keys_series[otherIdx]];
                            if (!otherSeriesGroup) return;
                            const otherKeys = Object.keys(otherSeriesGroup);
                            if (otherKeys.length === 0) return;
                            const firstOtherSerie = otherSeriesGroup[otherKeys[0]];
                            if (firstOtherSerie) {
                                syncCrosshair(otherChart, firstOtherSerie, dataPoint);
                            }
                        }
                    });
                });
            });
        } catch (errSyncChart) {
            console.error("Error errSyncChart:", errSyncChart);
        }
    };

    const clearAllSeries = () => {
        const chartRef = chartInstanceRefs.current;
        const serieRef = serieInstanceRefs.current;
        ['close', 'ex_refin', 'mkt_margin', 'oil_fund'].forEach((type, idx) => {
            const seriesMap = serieRef[type];
            Object.values(seriesMap).forEach(series => {
                try { chartRef[idx]?.removeSeries(series); } catch (e) { }
            });
            serieRef[type] = {};
        });
    };

    const handleChangeSymbol = (selectedValue) => {
        setParamsQuery(prev => ({ ...prev, symbol: selectedValue, queryType: 4 }));
    };

    const handleResetView = () => {
        chartInstanceRefs.current.forEach(chart => {
            if (chart) chart.timeScale().fitContent();
        });

        setTimeout(() => {
            const masterRange = chartInstanceRefs.current[0]?.timeScale().getVisibleLogicalRange();
            if (masterRange) {
                chartInstanceRefs.current.forEach(chart => {
                    if (chart) chart.timeScale().setVisibleLogicalRange(masterRange);
                });
            }
        }, 0);
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#0c111b', paddingTop: '20px' }}>

            <style>{`
                @keyframes symbolBounce {
                    0%   { transform: scale(1); }
                    30%  { transform: scale(1.04); }
                    50%  { transform: scale(0.98); }
                    70%  { transform: scale(1.02); }
                    100% { transform: scale(1); }
                }
                .symbol-bounce { animation: symbolBounce 1.8s ease-in-out infinite; }

                .oil-type-scroll::-webkit-scrollbar { width: 4px; }
                .oil-type-scroll::-webkit-scrollbar-track { background: transparent; }
                .oil-type-scroll::-webkit-scrollbar-thumb { background: #334155; border-radius: 2px; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>

            <div className="w-full text-white px-6 py-2">
                <div className="max-w-[1600px] mx-auto">

                    {/* ── TOP CONTROL BAR ── */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">

                        {/* Left: symbol + oil type */}
                        <div className="flex flex-wrap items-center gap-3 z-50">

                            {/* Symbol dropdown */}
                            <div className="relative w-56">
                                <div className={`relative bg-[#111827] border border-slate-700 rounded-md px-4 py-3 flex items-center ${!paramsQuery.symbol && !symbolQuery ? "symbol-bounce" : ""}`}>
                                    <input
                                        value={symbolQuery}
                                        onChange={e => { setSymbolQuery(e.target.value); setShowSymbolDropdown(true); }}
                                        onFocus={() => setShowSymbolDropdown(true)}
                                        placeholder=""
                                        className="w-full bg-transparent outline-none text-white text-sm"
                                    />
                                    <div className="flex items-center gap-2">
                                        {(paramsQuery.symbol || symbolQuery) && (
                                            <button onClick={() => { setSymbolQuery(""); handleChangeSymbol(""); }} className="text-slate-400 hover:text-white text-xs ml-2">✕</button>
                                        )}
                                        <span onClick={() => setShowSymbolDropdown(!showSymbolDropdown)} className="text-slate-400 text-xs ml-2 cursor-pointer">▾</span>
                                    </div>
                                </div>

                                <label className={`absolute left-4 px-2 transition-all duration-200 pointer-events-none ${paramsQuery.symbol || symbolQuery || showSymbolDropdown ? "-top-2 text-xs bg-[#0c111b]" : "top-1/2 -translate-y-1/2 text-sm text-slate-400"}`}>
                                    Symbol*
                                </label>

                                {showSymbolDropdown && (
                                    <div className="absolute mt-2 w-full bg-[#0f172a] border border-slate-700 rounded-xl shadow-2xl max-h-72 overflow-y-auto z-50">
                                        {filteredSymbols.length > 0 ? filteredSymbols.map((item, index) => (
                                            <div
                                                key={index}
                                                onClick={() => { setSymbolQuery(item); setShowSymbolDropdown(false); handleChangeSymbol(item); setIsSearch(true); }}
                                                className="px-4 py-2 text-sm text-slate-300 hover:bg-cyan-500 hover:text-white cursor-pointer transition"
                                            >{item}</div>
                                        )) : (
                                            <div className="px-4 py-2 text-sm text-slate-500">No results</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ── Oil type multi-select dropdown ── */}
                            <div className="relative w-72" ref={oilTypeDropdownRef}>
                                <div
                                    onClick={() => setShowOilTypeDropdown(prev => !prev)}
                                    className="bg-[#111827] border border-slate-700 rounded-md px-4 py-3 flex items-center justify-between cursor-pointer hover:border-cyan-400 transition-colors"
                                >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {selectedOptionsOilType.length === 0 ? (
                                            <span className="text-sm text-slate-400">Select oil type</span>
                                        ) : (
                                            <div className="flex flex-wrap gap-1 max-w-[180px]">
                                                {selectedOptionsOilType.slice(0, 2).map(opt => (
                                                    <span
                                                        key={opt}
                                                        className="bg-cyan-900 text-cyan-300 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
                                                    >
                                                        {opt.length > 10 ? opt.slice(0, 10) + '…' : opt}
                                                        <button
                                                            onClick={e => { e.stopPropagation(); handleToggleOilType(opt); }}
                                                            className="hover:text-white leading-none"
                                                        >✕</button>
                                                    </span>
                                                ))}
                                                {selectedOptionsOilType.length > 2 && (
                                                    <span className="bg-slate-700 text-slate-300 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                                                        +{selectedOptionsOilType.length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                        {selectedOptionsOilType.length > 0 && (
                                            <button
                                                onClick={e => { e.stopPropagation(); handleClearOilType(); }}
                                                className="text-slate-400 hover:text-white text-xs"
                                            >✕</button>
                                        )}
                                        <span className={`text-slate-400 text-xs transition-transform duration-200 ${showOilTypeDropdown ? 'rotate-180' : ''}`}>▾</span>
                                    </div>
                                </div>

                                {showOilTypeDropdown && (
                                    <div className="absolute mt-2 w-full bg-[#0f172a] border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                                        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
                                            <span className="text-xs text-slate-400">
                                                {selectedOptionsOilType.length} / {optionsOilType.length} selected
                                            </span>
                                            <button
                                                onClick={handleClearOilType}
                                                className="text-xs text-cyan-400 hover:text-cyan-300 transition"
                                            >
                                                Clear all
                                            </button>
                                        </div>

                                        <div className="max-h-64 overflow-y-auto oil-type-scroll">
                                            {optionsOilType.length === 0 ? (
                                                <div className="px-4 py-3 text-sm text-slate-500 text-center">No options available</div>
                                            ) : (
                                                optionsOilType.map(opt => {
                                                    const isSelected = selectedOptionsOilType.includes(opt);
                                                    return (
                                                        <div
                                                            key={opt}
                                                            onClick={() => handleToggleOilType(opt)}
                                                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors text-sm
                                                                ${isSelected
                                                                    ? 'bg-cyan-900/40 text-cyan-300'
                                                                    : 'text-slate-300 hover:bg-slate-800'
                                                                }`}
                                                        >
                                                            <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors
                                                                ${isSelected
                                                                    ? 'bg-cyan-500 border-cyan-500'
                                                                    : 'border-slate-600'
                                                                }`}>
                                                                {isSelected && (
                                                                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
                                                                        <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            <span className="truncate">{opt}</span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right: Reset + Period buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleResetView}
                                title="Fit to latest data"
                                className="px-3 py-1 text-xs rounded-md border border-slate-700 text-slate-400 hover:border-cyan-400 hover:text-cyan-400 transition-all"
                            >
                                🔄
                            </button>
                            {['3M', '6M', '1Y', 'YTD', 'ALL'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => setSelectedYear(p)}
                                    className={`px-3 py-1 text-xs rounded-md border transition-all
                                        ${selectedYear === p
                                            ? "bg-[#1f2937] border-cyan-400 text-cyan-400"
                                            : "border-slate-700 text-slate-400 hover:border-cyan-400 hover:text-cyan-400"
                                        }`}
                                >
                                    {p === 'ALL' ? 'MAX' : p}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── CHARTS GRID ── */}
                    <Grid
                        ref={resChartRef}
                        container
                        spacing={2}
                        sx={{
                            color: '#ffffff',
                            height: 'calc(100vh - 200px)',
                            minHeight: '600px',
                        }}
                    >
                        {[
                            { id: "chart-close",            delay: 0    },
                            { id: "chart-ex-refin",         delay: 0.15 },
                            { id: "chart-marketing-margin", delay: 0.30 },
                            { id: "chart-oil-fund",         delay: 0.45 },
                        ].map(({ id, delay }) => (
                            <Grid key={id} item xs={12} md={6} sx={{ height: '50%' }}>
                                <div className="relative bg-[#111827] border border-slate-700 rounded-xl overflow-hidden w-full h-full p-1">
                                    {isLoading && (
                                        <div className="absolute inset-0 z-10">
                                            <WaveSkeleton delay={delay} />
                                        </div>
                                    )}
                                    <div
                                        id={id}
                                        className="w-full h-full"
                                        ref={handleAddChartContainer}
                                        style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
                                    />
                                </div>
                            </Grid>
                        ))}
                    </Grid>

                </div>
            </div>
        </Box>
    );
};

export default PetroleumComponent;